import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/shared/AuthContext';
import PageContainer from '../components/common/PageContainer';
import PageHeader from '../components/common/PageHeader';
import PageSection from '../components/common/PageSection';

const TherapistAnalytics: React.FC = () => {
    const { user } = useAuth();
    const navigate = useNavigate();

    // Mock analytics data
    const analyticsData = {
        totalPatients: 24,
        activeAlerts: 3,
        avgResponseTime: 2.5,
        satisfactionScore: 4.7,
        weeklyEngagement: [85, 92, 78, 88, 95, 89, 91],
        emotionalSignals: {
            anxietyStress: 65,
            depression: 45,
            disconnection: 30,
            hopefulness: 75,
            motivation: 80,
            emotionalResilience: 70
        },
        monthlyTrends: {
            newPatients: [3, 5, 2, 4, 6, 3, 4],
            resolvedCases: [2, 3, 1, 4, 2, 3, 2],
            crisisInterventions: [1, 0, 2, 1, 0, 1, 0]
        },
        topConcerns: [
            { concern: 'Anxiety', count: 12, percentage: 50 },
            { concern: 'Depression', count: 8, percentage: 33 },
            { concern: 'Relationship Issues', count: 6, percentage: 25 },
            { concern: 'Work Stress', count: 4, percentage: 17 }
        ]
    };

    return (
        <PageContainer>
            <PageHeader 
                title="Analytics Dashboard" 
                emoji="📊"
                description="Comprehensive insights into patient progress and clinical outcomes"
            />
            
            {/* Key Metrics */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
                <div className="bg-white rounded-lg shadow p-6 text-center">
                    <div className="text-3xl mb-3">👥</div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Total Patients</h3>
                    <div className="text-4xl font-bold text-blue-600 mb-2">{analyticsData.totalPatients}</div>
                    <div className="text-sm text-gray-600 mb-1">Active Patients</div>
                    <div className="text-xs text-green-600">+3 this month</div>
                </div>
                
                <div className="bg-white rounded-lg shadow p-6 text-center">
                    <div className="text-3xl mb-3">🚨</div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Active Alerts</h3>
                    <div className="text-4xl font-bold text-red-600 mb-2">{analyticsData.activeAlerts}</div>
                    <div className="text-sm text-gray-600 mb-1">Require Attention</div>
                    <div className="text-xs text-orange-600">2 high priority</div>
                </div>
                
                <div className="bg-white rounded-lg shadow p-6 text-center">
                    <div className="text-3xl mb-3">⏱️</div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Avg Response Time</h3>
                    <div className="text-4xl font-bold text-green-600 mb-2">{analyticsData.avgResponseTime}h</div>
                    <div className="text-sm text-gray-600 mb-1">Response Time</div>
                    <div className="text-xs text-green-600">-0.5h vs last month</div>
                </div>
                
                <div className="bg-white rounded-lg shadow p-6 text-center">
                    <div className="text-3xl mb-3">⭐</div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Satisfaction</h3>
                    <div className="text-4xl font-bold text-purple-600 mb-2">{analyticsData.satisfactionScore}</div>
                    <div className="text-sm text-gray-600 mb-1">Patient Rating</div>
                    <div className="text-xs text-green-600">+0.2 vs last month</div>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Weekly Engagement Chart */}
                <PageSection title="Weekly Engagement" emoji="📈">
                    <div className="space-y-4">
                        <div className="flex items-center justify-between">
                            <span className="text-sm font-medium text-gray-700">Patient Activity</span>
                            <span className="text-sm text-gray-500">Last 7 days</span>
                        </div>
                        <div className="flex items-end space-x-2 h-32">
                            {analyticsData.weeklyEngagement.map((value, index) => (
                                <div key={index} className="flex-1 flex flex-col items-center">
                                    <div 
                                        className="w-full bg-blue-500 rounded-t"
                                        style={{ height: `${(value / 100) * 100}px` }}
                                    ></div>
                                    <span className="text-xs text-gray-500 mt-2">
                                        {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'][index]}
                                    </span>
                                </div>
                            ))}
                        </div>
                        <div className="text-center">
                            <span className="text-2xl font-bold text-blue-600">
                                {Math.round(analyticsData.weeklyEngagement.reduce((a, b) => a + b, 0) / analyticsData.weeklyEngagement.length)}%
                            </span>
                            <span className="text-sm text-gray-600 ml-2">Average Engagement</span>
                        </div>
                    </div>
                </PageSection>

                {/* Emotional Signals */}
                <PageSection title="Emotional Signals" emoji="🧠">
                    <div className="space-y-4">
                        {Object.entries(analyticsData.emotionalSignals).map(([key, value]) => (
                            <div key={key} className="flex items-center justify-between">
                                <span className="text-sm font-medium text-gray-700 capitalize">
                                    {key.replace(/([A-Z])/g, ' $1').trim()}
                                </span>
                                <div className="flex items-center space-x-2">
                                    <div className="w-24 bg-gray-200 rounded-full h-2">
                                        <div 
                                            className="bg-blue-500 h-2 rounded-full" 
                                            style={{ width: `${value}%` }}
                                        ></div>
                                    </div>
                                    <span className="text-sm font-medium text-gray-900 w-8">{value}%</span>
                                </div>
                            </div>
                        ))}
                    </div>
                </PageSection>
            </div>

            {/* Monthly Trends */}
            <PageSection title="Monthly Trends" emoji="📅">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div className="text-center">
                        <h3 className="font-semibold text-gray-900 mb-4">New Patients</h3>
                        <div className="flex items-end space-x-1 h-24">
                            {analyticsData.monthlyTrends.newPatients.map((value, index) => (
                                <div key={index} className="flex-1 flex flex-col items-center">
                                    <div 
                                        className="w-full bg-green-500 rounded-t"
                                        style={{ height: `${(value / 6) * 80}px` }}
                                    ></div>
                                    <span className="text-xs text-gray-500 mt-1">W{index + 1}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                    <div className="text-center">
                        <h3 className="font-semibold text-gray-900 mb-4">Resolved Cases</h3>
                        <div className="flex items-end space-x-1 h-24">
                            {analyticsData.monthlyTrends.resolvedCases.map((value, index) => (
                                <div key={index} className="flex-1 flex flex-col items-center">
                                    <div 
                                        className="w-full bg-blue-500 rounded-t"
                                        style={{ height: `${(value / 4) * 80}px` }}
                                    ></div>
                                    <span className="text-xs text-gray-500 mt-1">W{index + 1}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                    <div className="text-center">
                        <h3 className="font-semibold text-gray-900 mb-4">Crisis Interventions</h3>
                        <div className="flex items-end space-x-1 h-24">
                            {analyticsData.monthlyTrends.crisisInterventions.map((value, index) => (
                                <div key={index} className="flex-1 flex flex-col items-center">
                                    <div 
                                        className="w-full bg-red-500 rounded-t"
                                        style={{ height: `${(value / 2) * 80}px` }}
                                    ></div>
                                    <span className="text-xs text-gray-500 mt-1">W{index + 1}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </PageSection>

            {/* Top Concerns */}
            <PageSection title="Top Patient Concerns" emoji="🎯">
                <div className="space-y-4">
                    {analyticsData.topConcerns.map((concern, index) => (
                        <div key={index} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                            <div className="flex items-center space-x-3">
                                <span className="text-lg font-bold text-gray-600">#{index + 1}</span>
                                <div>
                                    <div className="font-medium text-gray-900">{concern.concern}</div>
                                    <div className="text-sm text-gray-600">{concern.count} patients</div>
                                </div>
                            </div>
                            <div className="flex items-center space-x-4">
                                <div className="text-right">
                                    <div className="text-2xl font-bold text-blue-600">{concern.percentage}%</div>
                                    <div className="text-xs text-gray-500">of patients</div>
                                </div>
                                <div className="w-20 bg-gray-200 rounded-full h-2">
                                    <div 
                                        className="bg-blue-500 h-2 rounded-full" 
                                        style={{ width: `${concern.percentage}%` }}
                                    ></div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </PageSection>

            {/* Quick Actions */}
            <PageSection title="Quick Actions" emoji="⚡">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <button 
                        onClick={() => navigate('/reports')}
                        className="p-4 bg-blue-100 hover:bg-blue-200 rounded-lg text-left transition-colors"
                    >
                        <div className="flex items-center space-x-3">
                            <span className="text-2xl">📊</span>
                            <div>
                                <div className="font-medium text-gray-900">Generate Report</div>
                                <div className="text-sm text-gray-600">Create detailed analytics report</div>
                            </div>
                        </div>
                    </button>
                    <button 
                        onClick={() => {
                            // Create a simple CSV export of the analytics data
                            const csvData = [
                                ['Metric', 'Value'],
                                ['Total Patients', analyticsData.totalPatients],
                                ['Active Alerts', analyticsData.activeAlerts],
                                ['Avg Session Duration', analyticsData.avgSessionDuration],
                                ['Patient Satisfaction', analyticsData.patientSatisfaction],
                                ['Treatment Success Rate', analyticsData.treatmentSuccessRate]
                            ];
                            const csvContent = csvData.map(row => row.join(',')).join('\n');
                            const blob = new Blob([csvContent], { type: 'text/csv' });
                            const url = window.URL.createObjectURL(blob);
                            const link = document.createElement('a');
                            link.href = url;
                            link.download = `analytics_export_${new Date().toISOString().split('T')[0]}.csv`;
                            link.click();
                            window.URL.revokeObjectURL(url);
                        }}
                        className="p-4 bg-green-100 hover:bg-green-200 rounded-lg text-left transition-colors"
                    >
                        <div className="flex items-center space-x-3">
                            <span className="text-2xl">📈</span>
                            <div>
                                <div className="font-medium text-gray-900">Export Data</div>
                                <div className="text-sm text-gray-600">Download analytics data</div>
                            </div>
                        </div>
                    </button>
                    <button 
                        onClick={() => navigate('/settings')}
                        className="p-4 bg-purple-100 hover:bg-purple-200 rounded-lg text-left transition-colors"
                    >
                        <div className="flex items-center space-x-3">
                            <span className="text-2xl">⚙️</span>
                            <div>
                                <div className="font-medium text-gray-900">Customize View</div>
                                <div className="text-sm text-gray-600">Adjust analytics settings</div>
                            </div>
                        </div>
                    </button>
                </div>
            </PageSection>
        </PageContainer>
    );
};

export default TherapistAnalytics;
